/**
 * ManageOverview.jsx
 * CRUD Manage Overview
 */

import { useState, useEffect } from "react";
import {
  BSModal,
  DeleteModal,
  Field,
  PageHeader,
  Loading,
  EmptyState,
  apiFetch,
} from "../../components/helpers";

const EMPTY_FORM = {
  overview_id: null,
  section_badge: "",
  heading_line1: "",
  heading_highlight: "",
  description: "",
  about_title: "",
  about_description: "",

  stat1_value: "",
  stat1_label: "",
  stat2_value: "",
  stat2_label: "",
  stat3_value: "",
  stat3_label: "",
  stat4_value: "",
  stat4_label: "",

  badge1: "",
  badge2: "",
  badge3: "",

  status: "Active",
};

export default function ManageOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);

    try {
      const json = await apiFetch(`${process.env.REACT_APP_OVERVIEW_VIEW}`);

      if (json.status) {
        setRows(json.data || []);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setForm(row);
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.heading_line1.trim()) return setError("Heading required");

    setSubmitting(true);
    setError("");

    try {
      const isEdit = !!form.overview_id;

      const json = await apiFetch(
        `${
          isEdit
            ? process.env.REACT_APP_OVERVIEW_UPDATE
            : process.env.REACT_APP_OVERVIEW_INSERT
        }`,
        {
          method: "POST",
          body: form,
        },
      );

      if (json.status) {
        setModalOpen(false);
        load();
      } else {
        setError(json.message || "Failed");
      }
    } catch {
      setError("Network Error");
    }

    setSubmitting(false);
  };

  const handleDelete = async () => {
    try {
      const json = await apiFetch(`${process.env.REACT_APP_OVERVIEW_DELETE}`, {
        method: "POST",
        body: {
          overview_id: deleteId,
        },
      });

      if (json.status) {
        setDeleteId(null);
        load();
      }
    } catch {}
  };

  return (
    <div className="p-3 p-lg-4">
      <PageHeader title="Manage Overview" subtitle={`${rows.length} record(s)`}>
        <button
          className="btn text-white fw-bold"
          style={{
            background: "#1D9E75",
            borderRadius: 10,
          }}
          onClick={openAdd}
        >
          + Add Overview
        </button>
      </PageHeader>

      <div
        className="card border-0 shadow-sm"
        style={{
          borderRadius: 16,
        }}
      >
        <div className="card-body p-0">
          {loading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState message="No data found" />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead
                  style={{
                    background: "#FAFBFC",
                  }}
                >
                  <tr>
                    {["Heading", "About", "Status", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-3">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r) => (
                    <tr key={r.overview_id}>
                      <td className="px-4">{r.heading_line1}</td>

                      <td className="px-4">{r.about_title}</td>

                      <td className="px-4">
                        <span
                          className={`badge ${
                            r.status === "Active"
                              ? "text-bg-success"
                              : "text-bg-secondary"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>

                      <td className="px-4">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => openEdit(r)}
                          >
                            Edit
                          </button>

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => setDeleteId(r.overview_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <BSModal
        open={modalOpen}
        title={form.overview_id ? "Edit Overview" : "Add Overview"}
        onClose={() => setModalOpen(false)}
      >
        <div className="row g-3">
          {[
            ["Section Badge", "section_badge"],
            ["Heading Line 1", "heading_line1"],
            ["Heading Highlight", "heading_highlight"],
            ["About Title", "about_title"],
            ["Stat1 Value", "stat1_value"],
            ["Stat1 Label", "stat1_label"],
            ["Stat2 Value", "stat2_value"],
            ["Stat2 Label", "stat2_label"],
            ["Stat3 Value", "stat3_value"],
            ["Stat3 Label", "stat3_label"],
            ["Stat4 Value", "stat4_value"],
            ["Stat4 Label", "stat4_label"],
            ["Badge1", "badge1"],
            ["Badge2", "badge2"],
            ["Badge3", "badge3"],
          ].map(([label, key]) => (
            <div className="col-md-6" key={key}>
              <Field label={label}>
                <input
                  className="form-control"
                  value={form[key]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [key]: e.target.value,
                    })
                  }
                />
              </Field>
            </div>
          ))}

          <div className="col-12">
            <Field label="Description">
              <textarea
                className="form-control"
                rows="3"
                value={form.description}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: e.target.value,
                  })
                }
              />
            </Field>
          </div>

          <div className="col-12">
            <Field label="About Description">
              <textarea
                className="form-control"
                rows="3"
                value={form.about_description}
                onChange={(e) =>
                  setForm({
                    ...form,
                    about_description: e.target.value,
                  })
                }
              />
            </Field>
          </div>

          <div className="col-12">
            <Field label="Status">
              <select
                className="form-select"
                value={form.status}
                onChange={(e) =>
                  setForm({
                    ...form,
                    status: e.target.value,
                  })
                }
              >
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </Field>
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3">{error}</div>}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="btn btn-light" onClick={() => setModalOpen(false)}>
            Cancel
          </button>

          <button
            className="btn text-white"
            style={{
              background: "#1D9E75",
            }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </BSModal>

      <DeleteModal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
